import {Injectable} from '@angular/core';
import {ProjectRestService} from '../../../api/services/project.rest.service';
import {forkJoin, Observable, of} from 'rxjs';
import {ProjectViewModel} from './project.view-model';
import {ResultListApiModel} from '../../../api/models/result-list.api.model';
import {ProjectApiModel} from '../../../api/models/project.api-model';
import {catchError, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import {StatusApiModel} from '../../../api/models/status.api.model';
import {StatusViewModel} from '../../../core/models/status.view-model';
import {VehicleRestService} from '../../../api/services/vehicle.rest.service';
import {VehicleTypeApiModel} from '../../../api/models/vehicle-type.api-model';
import {VehicleViewModel} from './vehicle.view-model';
import {VehicleApiModel} from '../../../api/models/vehicle.api-model';
import {VehicleTypeViewModel} from '../../../core/models/vehicle-type.view-model';
import {QueryParamsApiModel} from '../../../api/models/query-params-api.model';
import {FilterParameterApiModel} from '../../../api/models/filter-parameter.api.model';
import {GarageStatusEnum} from './garage-status.enum';
import {VehicleTypeRestService} from '../../../api/services/vehicle-type.rest.service';
import {CurrentUserViewModel} from '../../../core/models/current-user.view-model';
import {UserViewModel} from '../settings/user.view-model';
import {MediaObjectRestService} from '../../../api/services/media-object.rest.service';
import {MediaObjectViewModel} from '../../../core/models/media-object.view-model';
import {MediaObjectApiModel} from '../../../api/models/media-object.api-model';
import {ListingTileDataInterface} from '../../../shared/listing-tile/listing-tile-data.interface';
import {ProjectCollaborationViewModel} from '../collaboration/project-collaboration.view-model';
import {ProjectCollaborationDataProviderService} from '../collaboration/project-collaboration.data-provider.service';
import {ProjectCollaborationAssetViewModel} from '../collaboration/project-collaboration-asset.view-model';
import {ProjectCreationService} from './project-creation.service';
import {plainToClass} from 'class-transformer';

@Injectable()
export class GarageDataProviderService {
  public vehicleTypes: VehicleTypeViewModel[];
  public projects: ProjectViewModel[];
  public filteredProjects: ProjectViewModel[];
  public statuses: StatusViewModel[];
  public collaborations: ProjectCollaborationViewModel[] = [];
  public ownedCollaborations: ProjectCollaborationViewModel[] = [];
  private currentOwnerSlug: string | null = null;
  private currentOwnerId: string | null = null;
  private viewerSlug: string | null = null;
  private viewerId: string | null = null;

  constructor(
    private projectRS: ProjectRestService,
    private vehicleRS: VehicleRestService,
    private vehicleTypeRS: VehicleTypeRestService,
    private mediaObjectRS: MediaObjectRestService,
    private collaborationDPS: ProjectCollaborationDataProviderService,
    private projectCreation: ProjectCreationService
  ) {
  }

  public createProject(currentUserVM: CurrentUserViewModel, vehicleVM: VehicleViewModel): Observable<ProjectViewModel> {
    return this.projectCreation.create(currentUserVM, vehicleVM).pipe(tap((projectVM: ProjectViewModel) => {
      this.projects?.push(projectVM);
    }));
  }

  public addCreatedNativeProject(
    projectJson: string | undefined,
    vehicleIri: string | undefined,
    vehicleIdentity: string | undefined,
    vehicleTypeCode: string | undefined,
    currentUser: CurrentUserViewModel,
  ): ProjectViewModel | null {
    if (!projectJson || !vehicleIri || !vehicleIdentity || !currentUser?.['@id']) {
      return null;
    }
    let response: Record<string, unknown>;
    try {
      response = JSON.parse(projectJson);
    } catch {
      return null;
    }
    if (!response || typeof response !== 'object' || Array.isArray(response)
      || typeof response['@id'] !== 'string' || typeof response.slug !== 'string') {
      return null;
    }

    try {
      const projectAM = plainToClass(ProjectApiModel, response);
      projectAM.user = currentUser['@id'];
      projectAM.vehicle = vehicleIri;
      const vehicleAM = plainToClass(VehicleApiModel, {
        '@id': vehicleIri,
        id: Number(vehicleIri.split('/').pop()),
        fullVehicleIdentity: vehicleIdentity,
      });
      const vehicle = new VehicleViewModel(vehicleAM);
      if (vehicleTypeCode) {
        vehicle.vehicleType = new VehicleTypeViewModel(plainToClass(VehicleTypeApiModel, {
          code: vehicleTypeCode.toUpperCase(),
        }));
      }
      const project = this.projectCreation.fromCreatedResponse(projectAM, currentUser, vehicle);
      this.projects = [project, ...(this.projects || []).filter((item) =>
        !item.isPlaceholder && item['@id'] !== project['@id'])];
      this.filteredProjects = this.projects;
      return project;
    } catch {
      return null;
    }
  }

  public updateProject(projectVM: ProjectViewModel): Observable<ProjectViewModel> {
    return this.projectRS.put(projectVM.toApiModel())
      .pipe(map(() => {
        return projectVM;
      }));
  }

  public fetchResources(user: UserViewModel, preserveExisting = false): Observable<ProjectViewModel[]> {
    this.currentOwnerSlug = user.slug || null;
    this.currentOwnerId = user['@id'] || null;
    if (!preserveExisting) {
      this.projects = this.getPlaceholders();
      this.collaborations = [];
      this.ownedCollaborations = [];
    }
    return this.getOwnersResourceCollection(user)
      .pipe(
        mergeMap((projectVMs: ProjectViewModel[]) => {
          const ownedParams = new QueryParamsApiModel();
          ownedParams.filters.push(new FilterParameterApiModel('owner.slug', user.slug, 'single'));
          ownedParams.pagination = false;

          const ownedCollaborations$ = this.collaborationDPS.getCollection(ownedParams)
            .pipe(catchError(() => of([])));
          const myParams = new QueryParamsApiModel();
          myParams.pagination = false;
          if (this.currentOwnerSlug) {
            myParams.filters.push(new FilterParameterApiModel('collaborator.slug', this.currentOwnerSlug, 'single'));
          }
          const myCollaborations$ = this.currentOwnerSlug
            ? this.collaborationDPS.getCollection(myParams).pipe(
              catchError(() => this.isOwnerView()
                ? this.collaborationDPS.getMyCollection().pipe(catchError(() => of([])))
                : of([])
              )
            )
            : of([]);
          const assetsParams = new QueryParamsApiModel();
          assetsParams.pagination = false;
          const assets$ = this.collaborationDPS.getAssets(assetsParams).pipe(catchError(() => of([])));

          return forkJoin([of(projectVMs), ownedCollaborations$, myCollaborations$, assets$]).pipe(
            switchMap(([loadedProjects, ownedCollaborations, myCollaborations, collaborationAssets]:
              [ProjectViewModel[], ProjectCollaborationViewModel[], ProjectCollaborationViewModel[], ProjectCollaborationAssetViewModel[]]) => {
              const hydratedOwned = this.hydrateCollaborationAssets(ownedCollaborations, collaborationAssets);
              const hydratedSource = this.hydrateCollaborationAssets(myCollaborations, collaborationAssets);

              return forkJoin([
                of(loadedProjects),
                this.hydrateCollaborationCoverMedia(hydratedOwned),
                this.hydrateCollaborationCoverMedia(hydratedSource)
              ]);
            })
          );
        }),
        tap(([projectVMs, ownedCollaborations, myCollaborations]:
          [ProjectViewModel[], ProjectCollaborationViewModel[], ProjectCollaborationViewModel[]]) => {
          this.projects = projectVMs;
          this.filteredProjects = projectVMs;
          this.ownedCollaborations = ownedCollaborations;
          this.collaborations = this.mergeCollaborations(ownedCollaborations, myCollaborations);
        }),
        map(([projectVMs]) => projectVMs)
      );
  }

  public getCollaborationsForAllSection(): ProjectCollaborationViewModel[] {
    return this.collaborations || [];
  }

  public isViewerCollaborationParticipant(collaboration: ProjectCollaborationViewModel): boolean {
    if (!collaboration || !this.viewerId) {
      return false;
    }

    return collaboration.owner?.['@id'] === this.viewerId || collaboration.collaborator?.['@id'] === this.viewerId;
  }

  public setViewer(user: UserViewModel | null): void {
    this.viewerSlug = user?.slug || null;
    this.viewerId = user?.['@id'] || null;
  }

  public getCollection(): Observable<ProjectViewModel[]> {
    return this.projectRS.getCollection().pipe(map((resultListAM: ResultListApiModel<ProjectApiModel>) => {
      return resultListAM.records.map((projectAM: ProjectApiModel) => new ProjectViewModel(projectAM));
    }));
  }

  public getOwnersResourceCollection(user: UserViewModel): Observable<ProjectViewModel[]> {
    this.currentOwnerSlug = user.slug || null;
    const queryParams = new QueryParamsApiModel();
    queryParams.filters.push(new FilterParameterApiModel('owners.user.slug', user.slug, 'single'));
    queryParams.filters.push(new FilterParameterApiModel('user.slug', user.slug, 'single'));
    queryParams.pagination = false;
    return this.projectRS.getCollection(queryParams).pipe(mergeMap((resultListAM: ResultListApiModel<ProjectApiModel>) => {
      if (!resultListAM.records.length) {
        return of<ProjectViewModel[]>([]);
      }
      const deps = forkJoin([
        this.getVehicleCollection(resultListAM.records),
        this.getMediaObjectCollection(resultListAM.records)
      ]);
      return deps.pipe(map((
        [vehicleVMs, mediaObjectMVs]:
        [VehicleViewModel[], MediaObjectViewModel[]]) => {
        return resultListAM.records.map((projectAM: ProjectApiModel) => {
          const projectVM = new ProjectViewModel(projectAM);
          projectVM.vehicle = vehicleVMs.find(e => e['@id'] === projectAM.vehicle);
          projectVM.theme = mediaObjectMVs.find(e => e['@id'] === projectAM.theme);
          return projectVM;
        });
      }));
    }));
  }

  public getUserSubResourceCollection(user: UserViewModel): Observable<ProjectViewModel[]> {
    this.currentOwnerSlug = user.slug || null;
    return this.projectRS.getUserSubResourceCollection(user['@id']).pipe(mergeMap((resultListAM: ResultListApiModel<ProjectApiModel>) => {
      if (!resultListAM.records.length) {
        return of<ProjectViewModel[]>([]);
      }
      const deps = forkJoin([
        this.getVehicleCollection(resultListAM.records),
        this.getMediaObjectCollection(resultListAM.records)
      ]);
      return deps.pipe(map((
        [vehicleVMs, mediaObjectMVs]:
          [VehicleViewModel[], MediaObjectViewModel[]]) => {
        return resultListAM.records.map((projectAM: ProjectApiModel) => {
          const projectVM = new ProjectViewModel(projectAM);
          projectVM.vehicle = vehicleVMs.find(e => e['@id'] === projectAM.vehicle);
          projectVM.theme = mediaObjectMVs.find(e => e['@id'] === projectAM.theme);
          return projectVM;
        });
      }));
    }));
  }

  public getVehicleCollection(projectAMs: ProjectApiModel[]): Observable<VehicleViewModel[]> {
    if (!projectAMs.length) {
      return of<VehicleViewModel[]>([]);
    }
    const params = new QueryParamsApiModel();
    params.filters = projectAMs
      .filter((projectAM: ProjectApiModel) => !!projectAM.vehicle)
      .map(e => new FilterParameterApiModel('id', e.vehicle));
    if (!params.filters.length) {
      return of<VehicleViewModel[]>([]);
    }
    params.pagination = false;
    return this.vehicleRS.getVehicleCollection(params)
      .pipe(mergeMap((resultListAM: ResultListApiModel<VehicleApiModel>) => {
        return this.getVehicleTypeCollection().pipe(map((vehicleTypeVMs: VehicleTypeViewModel[]) => {
          this.vehicleTypes = vehicleTypeVMs;
          return resultListAM.records.map((vehicleAM: VehicleApiModel) => {
            const vehicleVM = new VehicleViewModel(vehicleAM);
            vehicleVM.vehicleType = vehicleTypeVMs.find(e => e['@id'] === vehicleAM.vehicleType);
            return vehicleVM;
          });
        }));
      }));
  }

  public getVehicleTypeCollection(queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<VehicleTypeViewModel[]> {
    return this.vehicleTypeRS.getVehicleTypeCollection(queryParamsApiModel)
      .pipe(map((resultListAM: ResultListApiModel<VehicleTypeApiModel>) => {
        return resultListAM.records.map((vehicleAM: VehicleTypeApiModel) => new VehicleTypeViewModel(vehicleAM));
      }));
  }

  public getMediaObjectCollection(projectAMs: ProjectApiModel[]): Observable<MediaObjectViewModel[]> {
    const params = new QueryParamsApiModel();
    params.filters = projectAMs
      .filter((e: ProjectApiModel) => typeof e.theme === 'string')
      .map(e => new FilterParameterApiModel('id', e.theme));
    if (params.filters.length) {
      return this.mediaObjectRS.getMediaObjectCollection(params)
        .pipe(map((resultListAM: ResultListApiModel<MediaObjectApiModel>) => {
          return resultListAM.records.map((mediaObjectAM: MediaObjectApiModel) => new MediaObjectViewModel(mediaObjectAM));
        }));
    }

    return of<MediaObjectViewModel[]>([]);
  }

  public uploadProjectTheme(listingTileTI: ListingTileDataInterface): Observable<ProjectViewModel> {
    return this.mediaObjectRS.uploadImage(listingTileTI.theme)
      .pipe(
        mergeMap((mediaObjectAM: MediaObjectApiModel) => {
          const projectAM = listingTileTI.refObject.toApiModel();
          projectAM.theme = mediaObjectAM['@id'];
          return this.projectRS.put(projectAM).pipe(map((projectAM: ProjectApiModel) => {
            const projectVM = listingTileTI.refObject.fromApiModel(projectAM);
            projectVM.theme = new MediaObjectViewModel(mediaObjectAM);
            return projectVM;
          }));
        })
      );
  }

  public removeProjectTheme(listingTileTI: ListingTileDataInterface): Observable<ProjectViewModel> {
    const projectAM = listingTileTI.refObject.toApiModel();
    return this.mediaObjectRS.deleteMediaObject(projectAM.theme)
      .pipe(
        map(() => {
            projectAM.theme = null;
            listingTileTI.refObject.theme = null;
            return listingTileTI.refObject;
        })
      );
  }

  public getStatusCollection(): Observable<StatusViewModel[]> {
    return this.projectRS.getStatusCollection()
      .pipe(map((resultListAM: ResultListApiModel<StatusApiModel>) => {
        return resultListAM.records.map((statusAM: StatusApiModel) => new StatusViewModel(statusAM));
      }));
  }

  public filterProjectListByStatus(status: string): ProjectViewModel[] {
    return this.filteredProjects = this.getProjectsByStatus(status);
  }

  public getProjectsByStatus(status: string): ProjectViewModel[] {
    return this.projects.filter((project: ProjectViewModel) => {
      if (project.isPlaceholder) {
        return true;
      }
      const currentOwnerType = this.getCurrentOwnerType(project);
      const isCurrentOwner = currentOwnerType !== null;
      const activeOwners = this.getActiveOwners(project);
      const hasActiveSecondary = activeOwners.some((owner) => owner?.type === 'SECONDARY');
      const isPendingCoOwner = this.isPendingCoOwner(project);

      project.isPendingCoOwner = isPendingCoOwner;

      if (isPendingCoOwner) {
        const isOwnerView = !!this.viewerSlug && !!this.currentOwnerSlug && this.viewerSlug === this.currentOwnerSlug;
        if (!isOwnerView) {
          return false;
        }
      }
      if (status === GarageStatusEnum.ALL) {
        return isCurrentOwner;
      }
      if (status === GarageStatusEnum.SOLD) {
        return this.isHistoricalOwner(project);
      }

      if (!isCurrentOwner) {
        return false;
      }

      if (status === GarageStatusEnum.INDIVIDUAL) {
        return currentOwnerType === 'MAIN' && !hasActiveSecondary;
      }

      if (status === GarageStatusEnum.CO_OWNER) {
        return currentOwnerType === 'SECONDARY' || hasActiveSecondary;
      }

      if (status === GarageStatusEnum.ON_SALE) {
        return project.isOnSale === true;
      }

      return false;
    });
  }

  public filterByINDIVIDUALStatus(): ProjectViewModel[] {
    return this.filterProjectListByStatus(GarageStatusEnum.INDIVIDUAL);
  }

  public filterByProjectSaleStatus(): ProjectViewModel[] {
    return this.filterProjectListByStatus(GarageStatusEnum.ON_SALE);
  }

  public filterByProjectCollaborationStatus(): ProjectViewModel[] {
    return this.filterProjectListByStatus(GarageStatusEnum.CO_OWNER);
  }

  public filterByProjectArchivedStatus(): ProjectViewModel[] {
    return this.filterProjectListByStatus(GarageStatusEnum.SOLD);
  }

  public onItemRemove(removedItem: ProjectViewModel) {
    const projectIndex = this.projects.indexOf(removedItem);
    this.projects.splice(projectIndex, 1);
  }

  public filterListByVehicleType(id): void {
    if (id === '') {
      this.filteredProjects = this.projects;
      return;
    }
    this.filteredProjects = this.projects.filter((e: ProjectViewModel) => {
      return e.vehicle.vehicleType.id === id;
    });
  }

  public getPlaceholders(): ProjectViewModel[] {
    return Array.from({length: 4}, (value, key) => {
      const projectViewModel = new ProjectViewModel();
      projectViewModel.isPlaceholder = true;
      projectViewModel.status = GarageStatusEnum.ALL;
      return projectViewModel;
    })
  }

  private isCurrentOwner(project: ProjectViewModel): boolean {
    return this.getCurrentOwnerType(project) !== null;
  }

  private getCurrentOwnerType(project: ProjectViewModel): 'MAIN' | 'SECONDARY' | null {
    if (project.isPlaceholder || !this.currentOwnerSlug) {
      return null;
    }

    const owner = this.getActiveOwners(project)
      .find((candidate) => this.ownerMatches(candidate, this.currentOwnerSlug, true));
    if (owner?.type === 'MAIN' || owner?.type === 'SECONDARY') {
      return owner.type;
    }

    if (this.userMatches(project.getApiModel()?.user, this.currentOwnerSlug)) {
      return 'MAIN';
    }

    return null;
  }

  private isPendingCoOwner(project: ProjectViewModel): boolean {
    if (project.isPlaceholder || !this.currentOwnerSlug) {
      return false;
    }
    const owner = this.getActiveOwners(project)
      .find((candidate) => this.ownerMatches(candidate, this.currentOwnerSlug, true));
    return owner?.type === 'SECONDARY' && !owner.confirmedAt;
  }

  private isHistoricalOwner(project: ProjectViewModel): boolean {
    if (project.isPlaceholder || !this.currentOwnerSlug) {
      return false;
    }

    return !!project.owners?.find((owner) => this.ownerMatches(owner, this.currentOwnerSlug, false));
  }

  private getActiveOwners(project: ProjectViewModel): any[] {
    if (!project.owners?.length) {
      return [];
    }

    return project.owners.filter((owner) => !owner.archivedAt);
  }

  private ownerMatches(owner, userSlug: string, onlyCurrent: boolean): boolean {
    if (!owner) {
      return false;
    }

    if (onlyCurrent && owner.archivedAt) {
      return false;
    }

    if (!onlyCurrent && !owner.archivedAt) {
      return false;
    }

    return this.userMatches(owner.user, userSlug);
  }

  private userMatches(user: any, userSlug: string): boolean {
    if (!user || !userSlug) {
      return false;
    }

    if (typeof user === 'string') {
      const parts = user.split('/');
      return parts[parts.length - 1] === userSlug;
    }

    return user?.slug === userSlug;
  }

  private hydrateCollaborationAssets(
    collaborations: ProjectCollaborationViewModel[],
    assets: ProjectCollaborationAssetViewModel[]
  ): ProjectCollaborationViewModel[] {
    const collaborationKeys = new Set((collaborations || [])
      .map((item) => this.resolveCollaborationOwnKey(item))
      .filter((key): key is string => !!key)
    );
    const assetsByCollaborationKey = new Map<string, ProjectCollaborationAssetViewModel[]>();

    (assets || []).forEach((asset) => {
      const collaborationKey = this.extractCollaborationKey(asset.collaboration);
      if (!collaborationKey || !collaborationKeys.has(collaborationKey)) {
        return;
      }

      const list = assetsByCollaborationKey.get(collaborationKey) || [];
      list.push(asset);
      assetsByCollaborationKey.set(collaborationKey, list);
    });

    return (collaborations || []).map((collaboration) => {
      const ownKey = this.resolveCollaborationOwnKey(collaboration);
      const list = [...(ownKey ? assetsByCollaborationKey.get(ownKey) || [] : [])];
      list.sort((a, b) => {
        const orderA = Number.isFinite(a.orderPosition) ? a.orderPosition : 0;
        const orderB = Number.isFinite(b.orderPosition) ? b.orderPosition : 0;
        if (orderA !== orderB) {
          return orderA - orderB;
        }

        const createdA = a.createdAt ? new Date(a.createdAt as any).getTime() : 0;
        const createdB = b.createdAt ? new Date(b.createdAt as any).getTime() : 0;
        return createdA - createdB;
      });

      collaboration.assets = list;
      const mappedCover = this.resolveCoverAssetFromList(collaboration.coverAsset, list);
      if (mappedCover) {
        collaboration.coverAsset = mappedCover;
      } else if ((!collaboration.coverAsset || !collaboration.coverAsset.media) && list.length) {
        collaboration.coverAsset = list[0];
      }

      return collaboration;
    });
  }

  private extractCollaborationKey(collaborationRef: string | {['@id']?: string; id?: number} | null | undefined): string | null {
    if (!collaborationRef) {
      return null;
    }

    if (typeof collaborationRef !== 'string' && Number.isFinite(Number(collaborationRef.id))) {
      return String(Number(collaborationRef.id));
    }

    const iri = typeof collaborationRef === 'string' ? collaborationRef : collaborationRef['@id'];
    if (!iri) {
      return null;
    }

    const parsedKey = String(iri).split('/').pop()?.trim() || '';
    return parsedKey || null;
  }

  private resolveCollaborationOwnKey(collaboration: ProjectCollaborationViewModel | null | undefined): string | null {
    if (!collaboration) {
      return null;
    }

    const slug = (collaboration.slug || '').trim();
    if (slug) {
      return slug;
    }

    if (Number.isFinite(Number(collaboration.id))) {
      return String(Number(collaboration.id));
    }

    return null;
  }

  private isOwnerView(): boolean {
    if (this.currentOwnerId && this.viewerId) {
      return this.currentOwnerId === this.viewerId;
    }

    return !!this.currentOwnerSlug && !!this.viewerSlug && this.currentOwnerSlug === this.viewerSlug;
  }

  private hydrateCollaborationCoverMedia(
    collaborations: ProjectCollaborationViewModel[]
  ): Observable<ProjectCollaborationViewModel[]> {
    const coverAssets = (collaborations || [])
      .map((collaboration) => collaboration.coverAsset)
      .filter((coverAsset): coverAsset is ProjectCollaborationAssetViewModel => !!coverAsset);

    const mediaIris = Array.from(new Set(coverAssets
      .map((coverAsset) => this.extractAssetMediaIri(coverAsset))
      .filter((iri): iri is string => !!iri)
      .filter((iri) => !this.hasAssetResolvedMedia(coverAssets.find((asset) => this.extractAssetMediaIri(asset) === iri))))
    );

    if (!mediaIris.length) {
      return of(collaborations || []);
    }

    const requests = mediaIris.map((iri) => this.mediaObjectRS.getMediaObject(iri).pipe(catchError(() => of(null))));

    return forkJoin(requests).pipe(
      map((mediaObjects: (MediaObjectApiModel | null)[]) => {
        const mediaMap = new Map<string, MediaObjectViewModel>();
        mediaObjects.forEach((mediaObject) => {
          if (!mediaObject?.['@id']) {
            return;
          }
          mediaMap.set(mediaObject['@id'], new MediaObjectViewModel(mediaObject));
        });

        (collaborations || []).forEach((collaboration) => {
          const mediaIri = this.extractAssetMediaIri(collaboration.coverAsset);
          if (!mediaIri || !mediaMap.has(mediaIri)) {
            return;
          }
          collaboration.coverAsset.media = mediaMap.get(mediaIri);
        });

        return collaborations || [];
      })
    );
  }

  private extractAssetMediaIri(asset: ProjectCollaborationAssetViewModel | null | undefined): string | null {
    const media = asset?.media as MediaObjectViewModel;
    if (media?.['@id']) {
      return media['@id'];
    }

    if (media?.id) {
      return `/api/media_objects/${media.id}`;
    }

    return null;
  }

  private hasAssetResolvedMedia(asset: ProjectCollaborationAssetViewModel | null | undefined): boolean {
    const media = asset?.media as MediaObjectViewModel;
    return !!media?.hyperlinks?.length;
  }

  private mergeCollaborations(
    ownedCollaborations: ProjectCollaborationViewModel[] = [],
    collaboratorCollaborations: ProjectCollaborationViewModel[] = []
  ): ProjectCollaborationViewModel[] {
    const merged = new Map<number | string, ProjectCollaborationViewModel>();

    [...ownedCollaborations, ...collaboratorCollaborations].forEach((collaboration) => {
      if (!collaboration?.id) {
        return;
      }
      merged.set(collaboration.id, collaboration);
    });

    return Array.from(merged.values()).sort((a, b) => {
      const aTime = a?.updatedAt ? new Date(a.updatedAt as any).getTime() : 0;
      const bTime = b?.updatedAt ? new Date(b.updatedAt as any).getTime() : 0;
      return bTime - aTime;
    });
  }

  private resolveCoverAssetFromList(
    coverAsset: ProjectCollaborationAssetViewModel | null | undefined,
    assets: ProjectCollaborationAssetViewModel[]
  ): ProjectCollaborationAssetViewModel | null {
    const coverId = this.extractAssetId(coverAsset);
    if (!coverId || !assets?.length) {
      return null;
    }

    return assets.find((asset) => this.extractAssetId(asset) === coverId) || null;
  }

  private extractAssetId(asset: ProjectCollaborationAssetViewModel | null | undefined): number | null {
    if (!asset) {
      return null;
    }

    if (Number.isFinite(Number(asset.id)) && Number(asset.id) > 0) {
      return Number(asset.id);
    }

    const iri = asset['@id'];
    if (!iri) {
      return null;
    }

    const parsedId = Number(String(iri).split('/').pop());
    return Number.isFinite(parsedId) ? parsedId : null;
  }
}
